/* ==========================================================================
   MIMI 2026 - SCORRIMENTO CONTINUO DELLA COLLEZIONE
   ==========================================================================
   I gioielli arrivano mentre si scorre, invece di far scegliere pagina 1, 2,
   3. La paginazione del tema resta scritta nel documento per i motori di
   ricerca (snippets/pagination.liquid), ritagliata via dallo schermo da
   assets/mimi-scorrimento.css.

   COME LAVORA
   1. Legge dal <nav class="pagination"> il collegamento rel="next": e' quello
      l'indirizzo della pagina successiva, con dentro gia' tutti i filtri e
      l'ordinamento in corso.
   2. Nasconde la paginazione e mette al suo posto il pulsante «Mostra altri».
   3. Un IntersectionObserver guarda quel pulsante: quando si avvicina al
      fondo dello schermo, con 800px di anticipo, chiede la pagina successiva.
   4. Della risposta prende le celle della griglia e le appende in fondo,
      poi rilegge da li' il nuovo rel="next".
   5. L'indirizzo del browser passa alla pagina appena caricata con
      history.replaceState: tornando indietro dalla scheda di un gioiello si
      resta al proprio posto.

   LE RICADUTE
   - JavaScript spento: la classe che ritaglia la paginazione la mette questo
     script, quindi la paginazione resta a video e funziona come sempre.
   - IntersectionObserver assente: il pulsante resta e si preme a mano.
   - Rete che cade o risposta storta: il pulsante torna attivo e la
     paginazione riappare, cosi' la strada resta aperta in ogni caso.

   I FILTRI
   snippets/facets.liquid porta <input type="hidden" name="page" value="">:
   il modulo dei filtri riparte quindi sempre dalla prima pagina, e il
   parametro «page» lasciato nell'indirizzo dallo scorrimento resta innocuo.
   Quando Search & Discovery ridisegna la collezione, il rel="next" cambia
   (i parametri del filtro sono altri): lo script se ne accorge da solo,
   riparte da capo e rimette il pulsante al punto giusto.

   LE TESSERE DI MOOD
   Prima di appendere, le tessere che completano l'ultima riga vengono tolte,
   cosi' i gioielli nuovi si mettono in coda ai gioielli e non alle immagini.
   A cose fatte assets/mimi-griglia-completa.js, che sorveglia il documento,
   rifa' il conto e le rimette in fondo.

   NIENTE GIRI A VUOTO
   L'aggancio si rifa' soltanto quando il rel="next" scritto nel documento e'
   diverso da quello gia' conosciuto. Le pagine caricate dallo scorrimento
   lasciano intatta la paginazione di partenza, quindi quel confronto resta
   fermo e le modifiche che lo script stesso provoca si spengono subito.
   ========================================================================== */

(() => {
  'use strict';

  const AMBITI = '.shopify-section--main-collection .collection__main';
  const GRIGLIA = 'product-list.product-list:not(.product-list--carousel)';
  const NAV = 'nav.pagination';
  const TESSERA = '[data-mimi-tessera]';
  const CASSETTO = '[data-mimi-altri]';

  const ETICHETTA = 'Mostra altri';
  const ETICHETTA_ATTESA = 'Un momento…';
  const ANTICIPO = '800px 0px';

  const osservatori = new WeakMap();
  let attesa = null;

  /* ------------------------------------------------------------------ letture */

  const prossimaScritta = (ambito) => {
    const nav = ambito.querySelector(NAV);
    if (nav == null) return '';
    const avanti = nav.querySelector('a[rel="next"]');
    return avanti == null ? '' : avanti.getAttribute('href') || '';
  };

  const idSezione = (ambito) => {
    const sezione = ambito.closest('.shopify-section');
    if (sezione == null || sezione.id.indexOf('shopify-section-') !== 0) return '';
    return sezione.id.slice('shopify-section-'.length);
  };

  const indirizzoDiSezione = (percorso, sezione) => {
    const indirizzo = new URL(percorso, window.location.href);
    if (sezione) indirizzo.searchParams.set('section_id', sezione);
    return indirizzo.toString();
  };

  const annuncia = (testo) => {
    const cassetto = document.getElementById('status-announcement');
    if (cassetto != null) cassetto.textContent = testo;
  };

  const scarica = (indirizzo) =>
    fetch(indirizzo, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((risposta) => {
        if (risposta.ok === false) throw new Error('risposta ' + risposta.status);
        return risposta.text();
      })
      .then((testo) => new DOMParser().parseFromString(testo, 'text/html'));

  /* ------------------------------------------------------------- la paginazione */

  const ritagliaPaginazione = (ambito) => {
    const nav = ambito.querySelector(NAV);
    if (nav == null) return;
    nav.classList.add('mimi-paginazione-nascosta');
    nav.setAttribute('aria-hidden', 'true');
    nav.querySelectorAll('a').forEach((voce) => voce.setAttribute('tabindex', '-1'));
  };

  const rimettiPaginazione = (ambito) => {
    const nav = ambito.querySelector(NAV);
    if (nav == null) return;
    nav.classList.remove('mimi-paginazione-nascosta');
    nav.removeAttribute('aria-hidden');
    nav.querySelectorAll('a').forEach((voce) => voce.removeAttribute('tabindex'));
  };

  /* ------------------------------------------------------------------ il carico */

  function carica(ambito) {
    const prossima = ambito.dataset.mimiProssima;
    if (!prossima) return;
    if (ambito.dataset.mimiInCorso === '1') return;
    ambito.dataset.mimiInCorso = '1';

    const cassetto = ambito.querySelector(CASSETTO);
    const pulsante = cassetto == null ? null : cassetto.querySelector('button');
    if (pulsante != null) {
      pulsante.disabled = true;
      pulsante.textContent = ETICHETTA_ATTESA;
    }

    const sezione = idSezione(ambito);

    scarica(indirizzoDiSezione(prossima, sezione))
      .then((documento) => {
        /* Se la resa della sola sezione arriva vuota, si chiede la pagina intera. */
        if (documento.querySelector(GRIGLIA) != null) return documento;
        return scarica(new URL(prossima, window.location.href).toString());
      })
      .then((documento) => {
        const arrivata = documento.querySelector(GRIGLIA);
        const griglia = ambito.querySelector(GRIGLIA);
        if (arrivata == null || griglia == null) throw new Error('griglia assente');

        /* Le tessere di mood escono: i gioielli nuovi vanno in coda ai gioielli. */
        griglia.querySelectorAll(TESSERA).forEach((tessera) => tessera.remove());

        const foglio = document.createDocumentFragment();
        let conto = 0;

        Array.from(arrivata.children).forEach((cella) => {
          if (cella.tagName === 'TEMPLATE') return;
          if (cella.matches(TESSERA)) return;

          const copia = document.importNode(cella, true);
          /* assets/theme.css tiene a zero l'opacita' di chi porta
             reveal-on-scroll finche' il tema lo rivela: qui l'attributo si
             toglie, cosi' i gioielli nuovi si vedono subito. */
          copia.removeAttribute('reveal-on-scroll');
          copia
            .querySelectorAll('[reveal-on-scroll]')
            .forEach((figlio) => figlio.removeAttribute('reveal-on-scroll'));

          foglio.appendChild(copia);
          conto += 1;
        });

        if (conto === 0) throw new Error('pagina vuota');
        griglia.appendChild(foglio);

        /* L'indirizzo segue il passo, il parametro di servizio resta fuori. */
        const indirizzo = new URL(prossima, window.location.href);
        indirizzo.searchParams.delete('section_id');
        window.history.replaceState(window.history.state, '', indirizzo.toString());

        const navArrivata = documento.querySelector(NAV);
        const avanti = navArrivata == null ? null : navArrivata.querySelector('a[rel="next"]');
        ambito.dataset.mimiProssima = avanti == null ? '' : avanti.getAttribute('href') || '';

        annuncia('Aggiunti ' + conto + ' gioielli.');

        if (ambito.dataset.mimiProssima) {
          if (pulsante != null) {
            pulsante.disabled = false;
            pulsante.textContent = ETICHETTA;
          }
          /* Il pulsante si rimette sotto osservazione: se dopo l'aggiunta e'
             rimasto in vista — succede quando la pagina arrivata portava
             poche schede — l'osservatore torna a parlare da solo, invece di
             aspettare che qualcuno scorra. */
          const osservatore = osservatori.get(ambito);
          if (osservatore != null && cassetto != null) {
            osservatore.unobserve(cassetto);
            osservatore.observe(cassetto);
          }
        } else {
          /* Tutti i gioielli sono a video: il pulsante lascia il posto. */
          const osservatore = osservatori.get(ambito);
          if (osservatore != null) {
            osservatore.disconnect();
            osservatori.delete(ambito);
          }
          if (cassetto != null) cassetto.remove();
        }
      })
      .catch(() => {
        /* La strada resta aperta: torna il pulsante e torna la paginazione. */
        if (pulsante != null) {
          pulsante.disabled = false;
          pulsante.textContent = ETICHETTA;
        }
        rimettiPaginazione(ambito);
      })
      .then(() => {
        ambito.dataset.mimiInCorso = '0';
      });
  }

  /* ------------------------------------------------------------- l'apparecchio */

  function prepara(ambito) {
    const origine = prossimaScritta(ambito);
    if (ambito.dataset.mimiOrigine === origine) return;
    ambito.dataset.mimiOrigine = origine;
    ambito.dataset.mimiProssima = origine;
    ambito.dataset.mimiInCorso = '0';

    const osservatoreVecchio = osservatori.get(ambito);
    if (osservatoreVecchio != null) {
      osservatoreVecchio.disconnect();
      osservatori.delete(ambito);
    }

    const cassettoVecchio = ambito.querySelector(CASSETTO);
    if (cassettoVecchio != null) cassettoVecchio.remove();

    if (!origine) {
      /* Una pagina sola: la paginazione del tema qui manca del tutto. */
      return;
    }

    ritagliaPaginazione(ambito);

    const cassetto = document.createElement('div');
    cassetto.className = 'mimi-altri';
    cassetto.setAttribute('data-mimi-altri', '');

    const pulsante = document.createElement('button');
    pulsante.type = 'button';
    pulsante.className = 'mimi-altri__pulsante';
    pulsante.textContent = ETICHETTA;
    pulsante.addEventListener('click', () => carica(ambito));

    cassetto.appendChild(pulsante);

    const nav = ambito.querySelector(NAV);
    if (nav != null && nav.parentNode === ambito) {
      ambito.insertBefore(cassetto, nav.nextSibling);
    } else {
      ambito.appendChild(cassetto);
    }

    if ('IntersectionObserver' in window) {
      const osservatore = new IntersectionObserver(
        (voci) => {
          voci.forEach((voce) => {
            if (voce.isIntersecting) carica(ambito);
          });
        },
        { rootMargin: ANTICIPO }
      );
      osservatore.observe(cassetto);
      osservatori.set(ambito, osservatore);
    }
  }

  function passa() {
    document.querySelectorAll(AMBITI).forEach(prepara);
  }

  function programma() {
    if (attesa !== null) return;
    attesa = requestAnimationFrame(() => {
      attesa = null;
      passa();
    });
  }

  /* Il documento cambia quando Search & Discovery ridisegna la collezione e
     quando l'editor del tema ricarica la sezione. */
  if ('MutationObserver' in window) {
    new MutationObserver(programma).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('shopify:section:load', programma);
  window.addEventListener('load', programma);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', programma);
  } else {
    programma();
  }
})();
